// src/pages/BO/SchoolEdit.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { schoolService, School } from '../../services/schoolService';

const containerStyle: React.CSSProperties = {
    padding: '2rem',
    background: '#f8f9fa',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
};

const formCardStyle: React.CSSProperties = {
    background: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    padding: '2rem',
    width: '100%',
    maxWidth: '600px',
};

const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: '1.5rem',
    textAlign: 'center',
};

const inputGroupStyle: React.CSSProperties = {
    marginBottom: '1.5rem',
};

const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 500,
    color: '#475569',
    fontSize: '0.9rem',
};

const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '6px',
    border: '1px solid #cbd5e1',
    fontSize: '1rem',
    color: '#334155',
    transition: 'border-color 0.2s',
    outline: 'none',
};

const buttonGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1rem',
    marginTop: '2rem',
};

const primaryButtonStyle: React.CSSProperties = {
    flex: 1,
    padding: '0.75rem',
    background: '#4f46e5',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 500,
    transition: 'background 0.2s',
};

const secondaryButtonStyle: React.CSSProperties = {
    flex: 1,
    padding: '0.75rem',
    background: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 500,
    transition: 'background 0.2s',
};

const SchoolEdit: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState<Partial<School>>({
        name: '',
        address: '',
        phone: '',
        email: '',
        enabled: true,
    });

    useEffect(() => {
        const load = async () => {
            try {
                if (!id) return;
                const schools = await schoolService.getSchools();
                const school = schools.find(s => s.id === id);
                if (school) {
                    setFormData(school);
                } else {
                    setError(t('school.notFound') || 'School not found');
                }
            } catch (e) {
                setError(t('school.loadFailed'));
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id, t]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id) return;
        setSaving(true);
        setError('');

        try {
            await schoolService.updateSchool(id, formData);
            navigate('/schools');
        } catch (e) {
            setError(t('school.updateFailed') || 'Failed to update school');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <div className="spinner"></div>
        </div>
    );

    if (error) return (
        <div style={{ padding: '2rem', color: '#ef4444', textAlign: 'center' }}>
            {error}
        </div>
    );

    return (
        <div style={containerStyle}>
            <div style={formCardStyle}>
                <h2 style={titleStyle}>{t('school.editSchool')}</h2>

                <form onSubmit={handleSubmit}>
                    <div style={inputGroupStyle}>
                        <label style={labelStyle}>{t('school.name')}</label>
                        <input
                            style={inputStyle}
                            value={formData.name || ''}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            required
                            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                            onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                        />
                    </div>

                    <div style={inputGroupStyle}>
                        <label style={labelStyle}>{t('school.address')}</label>
                        <input
                            style={inputStyle}
                            value={formData.address || ''}
                            onChange={e => setFormData({ ...formData, address: e.target.value })}
                            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                            onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div style={inputGroupStyle}>
                            <label style={labelStyle}>{t('school.phone')}</label>
                            <input
                                style={inputStyle}
                                value={formData.phone || ''}
                                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            />
                        </div>

                        <div style={inputGroupStyle}>
                            <label style={labelStyle}>{t('school.email')}</label>
                            <input
                                type="email"
                                style={inputStyle}
                                value={formData.email || ''}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                                onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            />
                        </div>
                    </div>

                    <div style={inputGroupStyle}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
                            <input
                                type="checkbox"
                                checked={formData.enabled}
                                onChange={e => setFormData({ ...formData, enabled: e.target.checked })}
                                style={{ marginRight: '0.75rem', width: '1.2rem', height: '1.2rem', accentColor: '#4f46e5' }}
                            />
                            <span style={{ color: '#334155', fontWeight: 500 }}>{t('school.enabled')}</span>
                        </label>
                    </div>

                    <div style={buttonGroupStyle}>
                        <button
                            type="button"
                            style={secondaryButtonStyle}
                            onClick={() => navigate('/schools')}
                            onMouseOver={(e) => e.currentTarget.style.background = '#e2e8f0'}
                            onMouseOut={(e) => e.currentTarget.style.background = '#f1f5f9'}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            style={primaryButtonStyle}
                            disabled={saving}
                            onMouseOver={(e) => e.currentTarget.style.background = '#4338ca'}
                            onMouseOut={(e) => e.currentTarget.style.background = '#4f46e5'}
                        >
                            {saving ? t('common.saving') : t('common.save')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SchoolEdit;
